import React, { useState, useEffect } from 'react';
import { FormControl, TextField } from '@mui/material';
import { useQuote } from "../../lib/contexts/QuoteContext";
import {
   getExpectedTransportIDLength,
   validateTransportID,
} from '../../lib/helpers/transportIdHelpers';
import useVINDetails from '../../lib/hooks/useVINDetails';
import {toUnderScoreSlug} from "../../lib/helpers/helpers";

const sdVehicleOptions = [
   {"value": "coupe_2_doors", "label": "Coupe (2 doors)"},
   {"value": "pickup_2_doors", "label": "Pickup (2 doors)"},
   {"value": "pickup_4_doors", "label": "Pickup (4 doors)"},
   {"value": "sedan", "label": "Sedan"},
   {"value": "suv", "label": "SUV"},
   {"value": "van", "label": "Van"},
]
/**
 * Map a single NHTSA VIN-decode result to one of our sdVehicleOptions.
 * @param {object} v - one element from Results[] of DecodeVinValues API
 * @returns {string} one of the sdVehicleOptions.value strings
 */
function mapNhtsaToSdType(v) {
   const doors = parseInt(v.Doors, 10) || 0;
   const bc = (v.BodyClass || "").toLowerCase();
   const vt = (v.VehicleType || "").toLowerCase();
   const combined = bc + " " + vt;
   
   // 1. Coupe
   if (combined.includes("coupe") || doors === 2) {
      return "coupe_2_doors";
   }
   
   // 2. Pickup / Truck
   if (combined.includes("pickup") || combined.includes("truck")) {
      return doors === 2 ? "pickup_2_doors" : "pickup_4_doors";
   }
   
   // 3. Sedan / Saloon
   if (combined.includes("sedan") || combined.includes("saloon")) {
      return "sedan";
   }
   
   // 4. SUV / MPV
   if (
      combined.includes("sport utility") ||
      combined.includes("suv") ||
      combined.includes("mpv") ||
      combined.includes("multipurpose passenger vehicle")
   ) {
      return "suv";
   }
   
   // 5. Van / Bus / Minivan
   if (
      combined.includes("van") ||
      combined.includes("bus") ||
      combined.includes("minivan")
   ) {
      return "van";
   }
   
   // 6. Fallback
   if (doors <= 2) {
      return "coupe_2_doors";
   }
   if (doors > 4) {
      return "suv";
   }
   return "sedan";
}

const TransportID = ({
                        mode = 'auto',
                        size,
                        value,
                        onChange = () => {},
                        viewMode = 'last_vin',
                        error = false,
                     }) => {
   const { vehicleYear, vehicleVIN, setVehicleVIN, setVehicleMake, setVehicleModel, setVehicleYear, setVehicleMakeModel, setVehicleType } = useQuote();
   const [internalError, setError] = useState('');
   
   const expectedLength = getExpectedTransportIDLength(mode);
   const isVINComplete = vehicleVIN.length === expectedLength;
   const { data } = useVINDetails(isVINComplete ? vehicleVIN : '', vehicleYear);
   
   useEffect(() => {
      const errorMsg = validateTransportID(vehicleVIN, mode);
      setError(errorMsg);
   }, [vehicleVIN, mode]);
   
   useEffect(() => {
      if (
         data?.Results?.[0]
        // && data.Results[0].ErrorCode === "0"
      ) {
         const result = data.Results[0];
         if (result.Make && result.Model && result.ModelYear) {
            setVehicleMake(result.Make);
            setVehicleModel(result.Model);
            setVehicleYear(parseInt(result.ModelYear));
            setVehicleMakeModel({ Make_Name: result.Make, Model_Name: result.Model });
            setVehicleType(sdVehicleOptions.find(o => o.value === mapNhtsaToSdType(result)).value);
         }
      }
   }, [data]);
   
   const handleChange = (e) => {
      const id = e.target.value.toUpperCase();
      if (id.length <= expectedLength) {
         setVehicleVIN(id);
         onChange({ target: { value: id } });
      }
   };
   
   return (
      <FormControl
         fullWidth
         margin="normal"
         error={error || !!internalError}
         size={size}
      >
         <TextField
            size={size}
            label={
               mode === 'auto'
                  ? size === 'small'
                     ? 'VIN'
                     : 'Vehicle VIN'
                  : size === 'small'
                     ? 'HIN'
                     : 'Boat HIN'
            }
            placeholder={
               mode === 'auto'
                  ? size === 'small'
                     ? 'VIN'
                     : 'Vehicle VIN'
                  : size === 'small'
                     ? 'HIN'
                     : 'Boat HIN'
            }
            variant="outlined"
            value={value || (viewMode !== 'new_vin' ? vehicleVIN : '')}
            onChange={handleChange}
            error={error || !!internalError}
            helperText={
               size !== 'small' &&
               (internalError ||
                  `Enter the ${mode === 'auto' ? 'Vehicle Identification Number' : 'Hull Identification Number'} (up to ${expectedLength} characters)`)
            }
            inputProps={{ maxLength: expectedLength }}
         />
      </FormControl>
   );
};

export default TransportID;
